import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import {
  canUseReaderCache,
  persistReaderEnvelope,
  readReaderCache,
  readScenarioEnvelope,
  scenarioEnvelopeError,
} from "../reader-envelope";
import type { components } from "../schema";

export type Scenario = components["schemas"]["Scenario"];

export type UseScenarioResult = UseQueryResult<Scenario, ApiError>;

// GET /scenarios/{scenarioId} を呼び、シナリオ情報（キャラクター一覧を含む）を返す query。
// 検証済みcacheはstaleTimeをInfinityにし、旧fixed/不正cacheはオンライン再取得する。
// scenarioId が null の場合はクエリ自体を無効化する。
export function useScenario(scenarioId: string | null): UseScenarioResult {
  const client = useApiClient();

  const result = useQuery<Scenario, ApiError>({
    queryKey: ["scenario", scenarioId],
    enabled: scenarioId !== null,
    staleTime: (query) => scenarioEnvelopeError(query.state.data, scenarioId ?? undefined) === null ? Infinity : 0,
    select: (data) => readScenarioEnvelope(data, scenarioId ?? undefined),
    // 5 分でガベージコレクト（useCurrentScene と同じ方針）
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      // enabled: false の状態なら queryFn は呼ばれないため、ここに到達した
      // 時点で scenarioId は必ず非 null。型を狭めるための保険的なガード。
      if (scenarioId === null) {
        throw new ApiError(0, {
          type: "about:blank",
          title: "Invalid state",
          status: 0,
          detail: "useScenario queryFn invoked without scenarioId",
        });
      }

      try {
        const { data, error } = await client.GET("/scenarios/{scenarioId}", {
          params: { path: { scenarioId } },
        });

        if (error !== undefined) {
          throw new ApiError(
            typeof (error as { status?: number }).status === "number"
              ? (error as { status: number }).status
              : 0,
            error as unknown as ApiError["problem"],
          );
        }
        const scenario = readScenarioEnvelope(data, scenarioId);
        persistReaderEnvelope("scenario", scenarioId, scenario);
        return scenario;
      } catch (err) {
        if (!canUseReaderCache(err)) throw err;
        const cached = await readReaderCache("scenario", scenarioId);
        if (cached !== null) return readScenarioEnvelope(cached, scenarioId);
        throw err;
      }
    },
  });
  if (result.isError) return { ...result, data: undefined, isLoadingError: true, isRefetchError: false };
  return result;
}
